import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown ";

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    itemsPerPage: number;
    totalItems: number;
    onItemsPerPageChange: (n: number) => void;
    itemsPerPageOptions?: number[];
}

export const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    itemsPerPage,
    totalItems,
    onItemsPerPageChange,
    itemsPerPageOptions = [5, 10, 25, 50]
}) => {
    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    const getVisiblePages = () => {
        const delta = 1; // Reduced delta for compactness
        const range = [];
        const rangeWithDots = [];

        for (
            let i = Math.max(2, currentPage - delta);
            i <= Math.min(totalPages - 1, currentPage + delta);
            i++
        ) {
            range.push(i);
        }
        if (currentPage - delta > 2) {
            rangeWithDots.push(1, '...');
        } else {
            rangeWithDots.push(1);
        }
        rangeWithDots.push(...range);
        if (currentPage + delta < totalPages - 1) {
            rangeWithDots.push('...', totalPages);
        } else {
            if (totalPages > 1) rangeWithDots.push(totalPages);
        }
        return rangeWithDots.filter((item, idx, arr) => arr.indexOf(item) === idx);
    };

    if (totalPages <= 1 && totalItems <= Math.min(...itemsPerPageOptions)) return null;

    return (
        <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/40 border-t border-gray-700/50 w-full">
            {/* Left Section: Stats & Items Per Page */}
            <div className="flex items-center space-x-3 text-[11px] text-gray-200">
                <div className="flex items-center space-x-1.5">
                    <ItemsPerPageDropdown
                        value={itemsPerPage}
                        onChange={onItemsPerPageChange}
                        options={itemsPerPageOptions}
                    />
                </div>
                <div className="h-3 w-px bg-gray-700" />
                <span className="font-medium">
                    Showing <span className="text-gray-300 font-bold">{startItem}-{endItem}</span> of <span className="text-gray-300 font-bold">{totalItems}</span>
                </span>


            </div>

            {/* Right Section: Page Navigation */}
            {totalPages > 1 && (
                <div className="flex items-center space-x-1">
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-1 rounded bg-gray-800 text-gray-200 hover:bg-gray-700 border border-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronLeft className="w-3.5 h-3.5" />
                    </button>

                    <div className="flex items-center px-1">
                        {getVisiblePages().map((page, idx) => (
                            <React.Fragment key={idx}>
                                {page === '...' ? (
                                    <span className="px-1.5 text-gray-600 text-[10px]">...</span>
                                ) : (
                                    <button
                                        onClick={() => typeof page === 'number' && onPageChange(page)}
                                        className={`min-w-[24px] h-6 px-1.5 rounded text-[11px] font-bold transition-all ${currentPage === page
                                            ? 'bg-orange-600 text-white shadow-sm'
                                            : 'text-gray-200 hover:text-gray-200 hover:bg-gray-700'
                                            }`}
                                    >
                                        {page}
                                    </button>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-1 rounded bg-gray-800 text-gray-200 hover:bg-gray-700 border border-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                        <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                </div>
            )}
        </div>
    );
};
